import { useRef } from 'react'
import Image from 'next/legacy/image'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { Trans, useTranslation } from 'next-i18next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import styled from 'styled-components'

import ENV_VARS from '@/config'
import { breakpoints, colors, fontSizes } from '@/styles'
import useOnScreen from '@/hooks/use-onscreen'
import Footer from '@/features/W1/Footer'
import Header from '@/features/W1/header'
import BannerImage from '@/features/W1/images/banner-2560.jpg'
import Bathroom from '@/features/W1/images/bathroom-406.jpg'
import Courtyard from '@/features/W1/images/courtyard-744.jpg'
import Drawer from '@/features/W1/images/drawer-258.png'
import Plan6 from '@/features/W1/images/east-tower/6.jpg'
import Plan7 from '@/features/W1/images/east-tower/7.jpg'
import Plan8 from '@/features/W1/images/east-tower/8.jpg'
import Plan9 from '@/features/W1/images/east-tower/9.jpg'
import Plan10 from '@/features/W1/images/east-tower/10.jpg'
import Kitchen from '@/features/W1/images/kitchen-1014.jpg'
import { ReactComponent as LivLogo } from '@/features/W1/images/liv-logo.svg'
import LogoW1 from '@/features/W1/images/logo-900.png'
import Plan1 from '@/features/W1/images/north-tower/1.jpg'
import Plan2 from '@/features/W1/images/north-tower/2.jpg'
import Plan3 from '@/features/W1/images/north-tower/3.jpg'
import Plan4 from '@/features/W1/images/north-tower/4.jpg'
import Plan5 from '@/features/W1/images/north-tower/5.jpg'
import markerIcon from '@/features/W1/images/skyline.png'
import W1Map from '@/features/W1/images/w1-map-696.jpg'
import StickyBar from '@/features/W1/sticky-bar'
import Styles from '@/features/W1/styles'
import { Anchor as AnchorLink } from '@/components/anchor'
import { AndroidButton, AppleButton } from '@/components/app-buttons'
import Head from '@/components/head'
import Map from '@/components/maplibre-map/map'
import Marker from '@/components/maplibre-map/marker'
import SupportOverlay from '@/components/support-overlay'
import { Tab, TabList, TabPanel, Tabs } from '@/components/tabs'

const { BASE_URL } = ENV_VARS

const Wrapper = styled.div``

const BannerButtonWrapper = styled.div`
  display: flex;
  justify-content: center;

  a:first-child {
    margin-right: 30px;
  }

  @media (max-width: 410px) {
    flex-wrap: wrap;

    a:first-child {
      margin-right: 0;
      margin-bottom: 25px;
    }
  }
`

const TabsContainer = styled(TabList)`
  box-shadow: none;
  margin-top: 16px;
  display: block;
`

const SingleTab = styled(Tab)`
  box-shadow: none;
  height: auto;
  color: #87724d;
  font-size: ${fontSizes.regular};
  line-height: 18px;
  font-weight: 500;
  padding: 2px;
  margin: 0 8px;
  font-family: 'Open Sans', sans-serif;

  &.active {
    border-bottom: 2px solid #87724d;
  }
`

const MapWrapper = styled.div`
  height: 400px;
  flex: 1;
`

const ListingTabList = styled(TabList)`
  width: 550px;
  box-shadow: none;
  justify-content: space-between;
  margin: 40px auto 0 auto;

  @media (max-width: ${breakpoints.tabletPortrait}) {
    width: 100%;
  }
`

const ListingTab = styled(Tab)`
  background: ${colors.white};
  color: #87724d;
  border: 1px solid #87724d;
  border-bottom: 0;
  font-size: 17px;
  line-height: 22px;
  letter-spacing: 0;
  height: 60px;
  padding-top: 3px;
  box-shadow: none;
  flex: 1;

  &.active {
    background: #87724d;
    color: ${colors.white};
    border: 0;
  }

  @media (max-width: ${breakpoints.phoneMax}) {
    border-left: 0;
    border-right: 0;
  }

  @media (max-width: 470px) {
    font-size: ${fontSizes.regularMobile};
  }

  @media (max-width: 400px) {
    padding: 0 20px;
  }
`

const ListingTabSmall = styled.span`
  font-size: 12px;
  display: block;
  letter-spacing: 0;

  @media (max-width: 400px) {
    display: none;
  }
`

const PlansList = styled.section`
  @media (min-width: 720px) {
    width: 700px;
    margin: 0 auto;
  }
`

const PlansTabsContainer = styled.section`
  border-bottom: 4px solid #87724d;
  margin-bottom: 40px;

  @media (max-width: ${breakpoints.tabletPortrait}) {
    margin-left: -20px;
    margin-right: -20px;
  }
`

const ListingsContent = styled.div`
  @media (min-width: ${breakpoints.tabletPortrait}) {
    width: 550px;
    margin: 0 auto;
  }
`

const TypeTitle = styled.h2`
  margin: 0;
  color: ${colors.darkGrey};
  font-size: ${fontSizes.regular};
  font-weight: bold;
  letter-spacing: 0.33px;
  line-height: 16px;
  text-align: left;
`

const ListingsTable = styled.table`
  border-collapse: separate;
  border-spacing: 0 30px;
  width: 440px;
  text-align: left;
  margin: 0 auto;

  td {
    vertical-align: middle;
  }

  @media (max-width: ${breakpoints.tabletPortrait}) {
    width: 100%;
  }
`

const Anchor = ({ href, children }) => (
  <Link
    href={href}
    rel="noopener noreferrer"
    target="_blank"
    passHref
    legacyBehavior
  >
    {children}
  </Link>
)

const PricingButton = styled(AnchorLink)`
  font-size: ${fontSizes.regular};
  line-height: 22px;
  color: ${colors.white};
  background: #87724d;
  height: 40px;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  text-decoration: none;

  @media (max-width: ${breakpoints.tabletPortrait}) {
    font-size: ${fontSizes.small};
  }
`

const PlanImageCell = styled.td`
  width: 100px;

  @media (max-width: ${breakpoints.tabletPortrait}) {
    width: 80px;
  }
`

const PlanNameCell = styled.td`
  width: 220px;

  strong {
    width: 80px;
    display: inline-block;
  }

  @media (max-width: ${breakpoints.tabletPortrait}) {
    width: 120px;

    strong {
      display: block;
    }
  }
`

const PlanButtonCell = styled.td`
  width: 120px;

  @media (max-width: ${breakpoints.tabletPortrait}) {
    font-size: ${fontSizes.small};
    width: 80px;
  }
`

const DescriptionText = styled.p`
  color: #87724d;
  font-size: ${fontSizes.regular};
  letter-spacing: 0.25px;
  line-height: 23px;
  text-align: center;
  margin: -40px auto 0;
  max-width: 900px;
  padding: 0 20px;

  @media (max-width: ${breakpoints.tabletPortrait}) {
    font-size: ${fontSizes.regularMobile};
  }
`

const AppBadgeWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 360px;
  margin-top: 35px;

  @media (max-width: ${breakpoints.tabletPortrait}) {
    width: 350px;
  }

  @media (max-width: 370px) {
    width: auto;
    flex-direction: column;

    a:first-child {
      margin-bottom: 20px;
    }
  }
`

const MarkerWrapper = styled.div`
  position: absolute;
  top: -42px;
  left: -19px;
`

const COORDS = [49.211198, -123.115799]

const MarkerComponent = () => (
  <MarkerWrapper>
    <Image src={markerIcon} width={56} height={56} alt="" loading="eager" />
  </MarkerWrapper>
)

const MapSection = () => (
  <section className="section-map">
    <MapWrapper>
      <Map center={COORDS} zoom={15}>
        <Marker position={COORDS} IconComponent={MarkerComponent} />
      </Map>
    </MapWrapper>
  </section>
)

const PlanImage = ({ src, ...rest }) => (
  <PlanImageCell {...rest}>
    <Image src={src} width={70} height={70} alt="" placeholder="blur" />
  </PlanImageCell>
)

const W1 = () => {
  const { t } = useTranslation('w1')
  const router = useRouter()

  const footer = useRef()
  const footerOnScreen = useOnScreen(footer, '-10px')

  const initialTab = router.route.includes('unfurnished')
    ? 'east-tower'
    : 'north-tower'

  const metaTitle = t('w1:page_title')
  const metaDescription = t('w1:page_description')
  const metaImage = `${BASE_URL}/images/w1-banner.jpg`

  return (
    <Wrapper>
      <Head
        title={metaTitle}
        description={metaDescription}
        meta={{
          image: metaImage,
          imageAlt: t('w1:W1 Vancouver Apartment Rentals'),
          keywords: t('w1:page_keywords'),
          type: 'website'
        }}
        path="/w1"
        hideCustomerSupport
      />
      <Styles />
      <Header />
      <SupportOverlay />
      <section className="banner-section">
        <div className="banner-content cta-buttons">
          <div className="box-title">
            <h1 className="banner-title">
              {t('w1:W1 Vancouver Apartment Rentals')}
            </h1>
            <h3 className="banner-subtitle">{t('w1:main.subtitle')}</h3>
            <BannerButtonWrapper>
              <Link
                href="https://liv.rent/building/6509/w1-north-tower-apartment-building-8080-nunavut-lane-vancouver-bc"
                passHref
                className="primary-button"
                id="w1-view-building-furnished-banner"
                legacyBehavior
              >
                {t('w1:View Furnished')}
              </Link>
              {/* <a
                className="primary-button"
                href="https://liv.rent/building/6506/w1-east-tower-apartment-building-433-sw-marine-dr-vancouver-bc"
                w1-east-tower-433-sw-marine-dr-vancouver-bc"
                id="w1-view-building-unfurnished-banner"
              >
                {t('w1:View Unfurnished')}
              </a> */}
            </BannerButtonWrapper>
          </div>
          <div className="banner-company-logo">
            <Image
              src={LogoW1}
              width={300}
              height={88}
              placeholder="blur"
              alt="Prompton Real Estate Services logo"
            />
          </div>
        </div>
        <div className="banner-image">
          <Image
            src={BannerImage}
            layout="fill"
            objectFit="cover"
            placeholder="blur"
            alt="W1 Apartment Rentals Marine and Cambie Vancouver"
          />
        </div>
      </section>
      <section className="cta-section">
        <h2 className="cta-section-title primary-title">
          {t('w1:Designed for long and short-term luxury living.')}
        </h2>
        <div className="cta-features">
          <div className="cta-feature-item">
            <header className="cta-feature-header">
              <i className="w1-icon map-location" />
              <h2 className="cta-feature-title">{t('w1:Centrally Located')}</h2>
            </header>
            <p className="cta-feature-text">
              {t(
                'w1:W1 is located conveniently between Vancouver and Richmond. Get to Downtown in 13 minutes and to YVR Airport in 9 minutes.'
              )}
            </p>
          </div>
          <div className="cta-feature-item">
            <header className="cta-feature-header">
              <i className="w1-icon meal" />
              <h2 className="cta-feature-title">{t('w1:Executive Rentals')}</h2>
            </header>
            <p className="cta-feature-text">
              {t(
                'w1:W1 executive rentals deliver a hotel-like experience. Beautifully furnished and unfurnished pet-friendly suites with luxury amenities are available for both long and short-term stays.'
              )}
            </p>
          </div>
          <div className="cta-feature-item">
            <header className="cta-feature-header">
              <i className="w1-icon shopping-bag" />
              <h2 className="cta-feature-title">{t('w1:Convenience')}</h2>
            </header>
            <p className="cta-feature-text">
              {t(
                'w1:Shopping, dining and entertainment at your doorstep! Unparalleled convenience to T&T Supermarket, Cineplex Theatre, RBC and many more!'
              )}
            </p>
          </div>
        </div>
        <div className="cta-action-box">
          <h2 className="secondary-title small">
            {/* {t(
              'w1:View all furnished and unfurnished apartment details below!'
            )} */}
            {t('w1:View all available floor plans below!')}
          </h2>
          <PlansList>
            <Tabs selectedInitialId={initialTab}>
              <PlansTabsContainer>
                <ListingTabList>
                  <ListingTab id="north-tower">
                    {t('w1:Furnished Apartments')}
                    <ListingTabSmall>{t('w1:North Tower')}</ListingTabSmall>
                  </ListingTab>
                  {/* <ListingTab id="east-tower">
                    {t('w1:Unfurnished Apartments')}
                    <ListingTabSmall>{t('w1:East Tower')}</ListingTabSmall>
                  </ListingTab> */}
                </ListingTabList>
              </PlansTabsContainer>
              <TabPanel id="north-tower">
                <ListingsContent>
                  <TypeTitle>
                    {t('w1:Bedrooms', {
                      count: 2
                    })}{' '}
                    - {t('common.furnished', 'Furnished')}
                  </TypeTitle>
                  <ListingsTable>
                    <tbody>
                      <tr>
                        <PlanImage src={Plan1} />
                        <PlanNameCell>
                          <strong>Plan C1</strong>
                          {t('w1:Bed', {
                            defaultValue: '{{count}} Bed',
                            defaultValue_plural: '{{count}} Beds',
                            count: 2
                          })}
                          ,{' '}
                          {t('w1:Bath', {
                            defaultValue: 'Bath',
                            defaultValue_plural: 'Baths',
                            count: 2
                          })}
                        </PlanNameCell>
                        <PlanButtonCell>
                          <Anchor href="https://liv.rent/building/6509/w1-north-tower-apartment-building-8080-nunavut-lane-vancouver-bc">
                            <PricingButton id="w1-view-listing-furnished-647">
                              {t('w1:See Pricing')}
                            </PricingButton>
                          </Anchor>
                        </PlanButtonCell>
                      </tr>
                      <tr>
                        <PlanImage src={Plan2} />
                        <PlanNameCell>
                          <strong>Plan D1</strong>
                          {t('w1:Bed', { count: 2 })},{' '}
                          {t('w1:Bath', { count: 2 })}
                        </PlanNameCell>
                        <PlanButtonCell>
                          <Anchor href="https://liv.rent/building/6509/w1-north-tower-apartment-building-8080-nunavut-lane-vancouver-bc">
                            <PricingButton id="w1-view-listing-furnished-650">
                              {t('w1:See Pricing')}
                            </PricingButton>
                          </Anchor>
                        </PlanButtonCell>
                      </tr>
                      <tr>
                        <PlanImage src={Plan3} />
                        <PlanNameCell>
                          <strong>Plan C1a</strong>
                          {t('w1:Bed', { count: 2 })},{' '}
                          {t('w1:Bath', { count: 1 })}
                        </PlanNameCell>
                        <PlanButtonCell>
                          <Anchor href="https://liv.rent/building/6509/w1-north-tower-apartment-building-8080-nunavut-lane-vancouver-bc">
                            <PricingButton id="w1-view-listing-furnished-652">
                              {t('w1:See Pricing')}
                            </PricingButton>
                          </Anchor>
                        </PlanButtonCell>
                      </tr>
                    </tbody>
                  </ListingsTable>
                  <TypeTitle>
                    {t('w1:Bedrooms', {
                      count: 3
                    })}{' '}
                    - {t('common.furnished', 'Furnished')}
                  </TypeTitle>
                  <ListingsTable>
                    <tbody>
                      <tr>
                        <PlanImage src={Plan4} />
                        <PlanNameCell>
                          <strong>Plan D3</strong>
                          {t('w1:Bed', { count: 3 })},{' '}
                          {t('w1:Bath', { count: 2 })}
                        </PlanNameCell>
                        <PlanButtonCell>
                          <Anchor href="https://liv.rent/building/6509/w1-north-tower-apartment-building-8080-nunavut-lane-vancouver-bc">
                            <PricingButton id="w1-view-listing-furnished-649">
                              {t('w1:See Pricing')}
                            </PricingButton>
                          </Anchor>
                        </PlanButtonCell>
                      </tr>
                      <tr>
                        <PlanImage src={Plan5} />
                        <PlanNameCell>
                          <strong>Plan D2</strong>
                          {t('w1:Bed', { count: 3 })},{' '}
                          {t('w1:Bath', { count: 2 })}
                        </PlanNameCell>
                        <PlanButtonCell>
                          <Anchor href="https://liv.rent/building/6509/w1-north-tower-apartment-building-8080-nunavut-lane-vancouver-bc">
                            <PricingButton id="w1-view-listing-furnished-653">
                              {t('w1:See Pricing')}
                            </PricingButton>
                          </Anchor>
                        </PlanButtonCell>
                      </tr>
                    </tbody>
                  </ListingsTable>
                </ListingsContent>
              </TabPanel>
              <TabPanel id="east-tower">
                <ListingsContent>
                  <TypeTitle>
                    {t('w1:Bedrooms', {
                      defaultValue: '{{count}} Bedroom',
                      defaultValue_plural: '{{count}} Bedrooms',
                      count: 1
                    })}{' '}
                    - {t('common.unfurnished', 'Unfurnished')}
                  </TypeTitle>
                  <ListingsTable>
                    <tbody>
                      <tr>
                        <PlanImage src={Plan6} />
                        <PlanNameCell>
                          <strong>B1a</strong>
                          {t('w1:Bed', { count: 1 })},{' '}
                          {t('w1:Bath', { count: 1 })}
                        </PlanNameCell>
                        <PlanButtonCell>
                          <Anchor href="https://liv.rent/building/6509/w1-north-tower-apartment-building-8080-nunavut-lane-vancouver-bc">
                            <PricingButton id="w1-view-listing-unfurnished-882">
                              {t('w1:See Pricing')}
                            </PricingButton>
                          </Anchor>
                        </PlanButtonCell>
                      </tr>
                      <tr>
                        <PlanImage src={Plan7} />
                        <PlanNameCell>
                          <strong>B1e</strong>
                          {t('w1:Bed', { count: 1 })},{' '}
                          {t('w1:Bath', { count: 1 })}
                        </PlanNameCell>
                        <PlanButtonCell>
                          <Anchor href="https://liv.rent/building/6509/w1-north-tower-apartment-building-8080-nunavut-lane-vancouver-bc">
                            <PricingButton id="w1-view-listing-unfurnished-883">
                              {t('w1:See Pricing')}
                            </PricingButton>
                          </Anchor>
                        </PlanButtonCell>
                      </tr>
                    </tbody>
                  </ListingsTable>
                  <TypeTitle>
                    {t('w1:Bedrooms', {
                      count: 2
                    })}{' '}
                    - {t('common.unfurnished', 'Unfurnished')}
                  </TypeTitle>
                  <ListingsTable>
                    <tbody>
                      <tr>
                        <PlanImage src={Plan8} />
                        <PlanNameCell>
                          <strong>C2b</strong>
                          {t('w1:Bed', { count: 2 })},{' '}
                          {t('w1:Bath', { count: 2 })}
                        </PlanNameCell>
                        <PlanButtonCell>
                          <Anchor href="https://liv.rent/building/6509/w1-north-tower-apartment-building-8080-nunavut-lane-vancouver-bc">
                            <PricingButton id="w1-view-listing-unfurnished-884">
                              {t('w1:See Pricing')}
                            </PricingButton>
                          </Anchor>
                        </PlanButtonCell>
                      </tr>
                      <tr>
                        <PlanImage src={Plan9} />
                        <PlanNameCell>
                          <strong>C1</strong>
                          {t('w1:Bed', { count: 2 })},{' '}
                          {t('w1:Bath', { count: 2 })}
                        </PlanNameCell>
                        <PlanButtonCell>
                          <Anchor href="https://liv.rent/building/6509/w1-north-tower-apartment-building-8080-nunavut-lane-vancouver-bc">
                            <PricingButton id="w1-view-listing-unfurnished-885">
                              {t('w1:See Pricing')}
                            </PricingButton>
                          </Anchor>
                        </PlanButtonCell>
                      </tr>
                    </tbody>
                  </ListingsTable>
                  <TypeTitle>
                    {t('w1:Bedrooms', {
                      count: 3
                    })}{' '}
                    - {t('common.unfurnished', 'Unfurnished')}
                  </TypeTitle>
                  <ListingsTable>
                    <tbody>
                      <tr>
                        <PlanImage src={Plan10} />
                        <PlanNameCell>
                          <strong>D1</strong>
                          {t('w1:Bed', { count: 3 })},{' '}
                          {t('w1:Bath', { count: 2 })}
                        </PlanNameCell>
                        <PlanButtonCell>
                          <Anchor href="https://liv.rent/building/6509/w1-north-tower-apartment-building-8080-nunavut-lane-vancouver-bc">
                            <PricingButton id="w1-view-listing-unfurnished-886">
                              {t('w1:See Pricing')}
                            </PricingButton>
                          </Anchor>
                        </PlanButtonCell>
                      </tr>
                    </tbody>
                  </ListingsTable>
                </ListingsContent>
              </TabPanel>
            </Tabs>
          </PlansList>
        </div>
      </section>
      <section className="content-section container">
        <DescriptionText>
          <Trans i18nKey="w1:Main Building Description">
            W1 luxury rental apartments offer visitors and residents the
            opportunity to live in beautifully furnished or unfurnished rental
            apartments. Enjoy premium amenities, a central location and flexible
            lease arrangements for 2 and 3 bedroom furnished suites. This
            rental-only building offers both monthly apartment rentals – perfect
            for corporate stays - as well as longer-term leases for maximum
            flexibility.
          </Trans>
        </DescriptionText>
      </section>
      <section className="content-section container">
        <div className="content-left tablet-hidden">
          <div className="come-home">
            <h3 className="text first">{t('w1:COME HOME')}</h3>
            <h3 className="text last">{t('w1:IN STYLE')}</h3>
          </div>
          <div className="images mobile-hidden">
            <Image
              src={Bathroom}
              width={407}
              height={639}
              placeholder="blur"
              alt="W1 Apartments Vancouver Marine and Cambie Bathroom"
            />
          </div>
        </div>
        <div className="content-right">
          <Image
            src={Kitchen}
            width={1013}
            height={570}
            placeholder="blur"
            alt="W1 Apartments Vancouver Marine and Cambie Kitchen"
          />
          <div className="bottom">
            <div className="images">
              <div className="tablet-only tablet-left">
                <Image
                  src={Bathroom}
                  width={211}
                  height={332}
                  placeholder="blur"
                  alt="W1 Apartments Vancouver Marine and Cambie Bathroom"
                />
              </div>
              <div className="mobile-only xs-left">
                <Image
                  src={Bathroom}
                  width={366}
                  height={575}
                  placeholder="blur"
                  alt="W1 Apartments Vancouver Marine and Cambie Bathroom"
                />
              </div>
              <div className="xs-right">
                <Image
                  src={Drawer}
                  width={258}
                  height={460}
                  placeholder="blur"
                  alt="Kitchen drawer"
                />
              </div>
            </div>
            <div className="features-section">
              <h2 className="secondary-title">
                {t('w1:Included Unit Features')}:
              </h2>
              <div className="features-container">
                <ul className="features-list five-per-row">
                  <li className="feature-item">
                    <i className="w1-icon bed" />
                    <Trans i18nKey="w1:w1.features.bed">
                      <span className="feature-item-text">Queen or</span>
                      <span className="feature-item-text">Double Beds</span>
                    </Trans>
                  </li>
                  <li className="feature-item">
                    <i className="w1-icon air-conditioning" />
                    <Trans i18nKey="w1:w1.features.air_conditioning">
                      <span className="feature-item-text">Air</span>
                      <span className="feature-item-text">Conditioning</span>
                    </Trans>
                  </li>
                  <li className="feature-item">
                    <i className="w1-icon fridge" />
                    <span className="feature-item-text">
                      {t('w1:w1.features.fridge', 'Fridge')}
                    </span>
                  </li>
                  <li className="feature-item">
                    <i className="w1-icon stove" />
                    <Trans i18nKey="w1:w1.features.stove_and_oven">
                      <span className="feature-item-text">Stove &</span>
                      <span className="feature-item-text">Oven</span>
                    </Trans>
                  </li>
                  <li className="feature-item">
                    <i className="w1-icon dishwasher" />
                    <span className="feature-item-text">
                      {t('w1:w1.features.dishwasher', 'Dishwasher')}
                    </span>
                  </li>
                  <li className="feature-item">
                    <i className="w1-icon washer" />
                    <Trans i18nKey="w1:w1.features.insuite_washer">
                      <span className="feature-item-text">In-suite</span>
                      <span className="feature-item-text">Washer</span>
                    </Trans>
                  </li>
                  <li className="feature-item">
                    <i className="w1-icon dryer" />
                    <Trans i18nKey="w1:w1.features.insuite_dryer">
                      <span className="feature-item-text">In-suite</span>
                      <span className="feature-item-text">Dryer</span>
                    </Trans>
                  </li>
                  <li className="feature-item">
                    <i className="w1-icon balcony" />
                    <Trans i18nKey="w1:w1.features.balcony_w_furniture">
                      <span className="feature-item-text">Balcony w/</span>
                      <span className="feature-item-text">Furniture</span>
                    </Trans>
                  </li>
                  <li className="feature-item">
                    <i className="w1-icon cable" />
                    <Trans i18nKey="w1:w1.features.tv_and_cable">
                      <span className="feature-item-text">TV and</span>
                      <span className="feature-item-text">Cable</span>
                    </Trans>
                  </li>
                  <li className="feature-item">
                    <i className="w1-icon wifi" />
                    <Trans i18nKey="w1:w1.features.highspeed_internet">
                      <span className="feature-item-text">High-speed</span>
                      <span className="feature-item-text">Internet</span>
                    </Trans>
                  </li>
                </ul>
              </div>
              <p className="disclaimer tablet-hidden">
                <Trans i18nKey="w1:Features Disclaimer">
                  **Disclaimer: Furnishing may depend on the type of units
                  available. Please see each of the listing details for more
                  information.
                </Trans>
              </p>
            </div>
          </div>
        </div>
      </section>
      <section className="content-footer amenities container">
        <div className="content-item left">
          <div className="features-section full">
            <h2 className="secondary-title">{t('w1:Building Amenities')}:</h2>

            <Tabs selectedInitialId={initialTab}>
              <TabsContainer>
                <SingleTab id="north-tower">
                  {t('common.furnished', 'Furnished')}
                </SingleTab>
                <SingleTab id="east-tower">
                  {t('common.unfurnished', 'Unfurnished')}
                </SingleTab>
              </TabsContainer>
              <TabPanel id="north-tower">
                <div className="features-container">
                  <ul
                    id="north-tower"
                    className="features-list tab-content visible smaller"
                  >
                    <li className="feature-item">
                      <i className="w1-icon parking" />
                      <Trans i18nKey="w1:w1.amenities.secure_parking">
                        <span className="feature-item-text">Secure</span>
                        <span className="feature-item-text">Parking</span>
                      </Trans>
                    </li>
                    <li className="feature-item">
                      <i className="w1-icon wifi" />
                      <Trans i18nKey="w1:w1.amenities.building_wide_wifi">
                        <span className="feature-item-text">Building-wide</span>
                        <span className="feature-item-text">WiFi</span>
                      </Trans>
                    </li>
                    <li className="feature-item">
                      <i className="w1-icon amenity-lounge" />
                      <Trans i18nKey="w1:w1.amenities.amenity_lounge">
                        <span className="feature-item-text">Amenity</span>
                        <span className="feature-item-text">Lounge</span>
                      </Trans>
                    </li>
                    <li className="feature-item">
                      <i className="w1-icon caretaker" />
                      <Trans i18nKey="w1:w1.amenities.building_manager">
                        <span className="feature-item-text">Building</span>
                        <span className="feature-item-text">Manager</span>
                      </Trans>
                    </li>
                    <li className="feature-item">
                      <i className="w1-icon rooftop-patio" />
                      <Trans i18nKey="w1:w1.amenities.rooftop_patio">
                        <span className="feature-item-text">Rooftop</span>
                        <span className="feature-item-text">Patio</span>
                      </Trans>
                    </li>
                    <li className="feature-item">
                      <i className="w1-icon pets" />
                      <Trans i18nKey="w1:w1.amenities.pet_friendly_rental">
                        <span className="feature-item-text">Pet Friendly</span>
                        <span className="feature-item-text">Rental</span>
                      </Trans>
                    </li>
                    <li className="feature-item">
                      <i className="w1-icon kids-area" />
                      <Trans i18nKey="w1:w1.amenities.kids_play_area">
                        <span className="feature-item-text">Kids Play</span>
                        <span className="feature-item-text">Area</span>
                      </Trans>
                    </li>
                    <li className="feature-item">
                      <i className="w1-icon courtyard" />
                      <Trans i18nKey="w1:w1.amenities.private_outdoor_area">
                        <span className="feature-item-text">Private</span>
                        <span className="feature-item-text">Outdoor Area</span>
                      </Trans>
                    </li>
                  </ul>
                </div>
              </TabPanel>
              <TabPanel id="east-tower">
                <div className="features-container">
                  <ul
                    id="east-tower"
                    className="features-list six-per-row tab-content visible"
                  >
                    <li className="feature-item">
                      <i className="w1-icon parking" />
                      <Trans i18nKey="w1:w1.amenities.secure_parking">
                        <span className="feature-item-text">Secure</span>
                        <span className="feature-item-text">Parking</span>
                      </Trans>
                    </li>
                    <li className="feature-item">
                      <i className="w1-icon room" />
                      <Trans i18nKey="w1:w1.amenities.tutor_card_room">
                        <span className="feature-item-text">Tutor / Card</span>
                        <span className="feature-item-text">Room</span>
                      </Trans>
                    </li>
                    <li className="feature-item">
                      <i className="w1-icon meeting-room" />
                      <Trans i18nKey="w1:w1.amenities.meeting_room">
                        <span className="feature-item-text">Meeting</span>
                        <span className="feature-item-text">Room</span>
                      </Trans>
                    </li>
                    <li className="feature-item">
                      <i className="w1-icon caretaker" />
                      <Trans i18nKey="w1:w1.amenities.concierge_services">
                        <span className="feature-item-text">Concierge</span>
                        <span className="feature-item-text">Services</span>
                      </Trans>
                    </li>
                    <li className="feature-item">
                      <i className="w1-icon amenity-lounge" />
                      <Trans i18nKey="w1:w1.amenities.amenity_arts_lounge">
                        <span className="feature-item-text">
                          Amenity / Arts
                        </span>
                        <span className="feature-item-text">Lounge</span>
                      </Trans>
                    </li>
                    <li className="feature-item">
                      <i className="w1-icon media-room" />
                      <Trans i18nKey="w1:w1.amenities.karaoke_screening_room">
                        <span className="feature-item-text">Karaoke /</span>
                        <span className="feature-item-text">
                          Screening Room
                        </span>
                      </Trans>
                    </li>
                    <li className="feature-item">
                      <i className="w1-icon rooftop-patio" />
                      <Trans i18nKey="w1:w1.amenities.rooftop_patio">
                        <span className="feature-item-text">Rooftop</span>
                        <span className="feature-item-text">Patio</span>
                      </Trans>
                    </li>
                    <li className="feature-item">
                      <i className="w1-icon pets" />
                      <Trans i18nKey="w1:w1.amenities.pet_friendly_rental">
                        <span className="feature-item-text">Pet Friendly</span>
                        <span className="feature-item-text">Rental</span>
                      </Trans>
                    </li>
                    <li className="feature-item">
                      <i className="w1-icon kids-area" />
                      <Trans i18nKey="w1:w1.amenities.kids_play_area">
                        <span className="feature-item-text">Kids Play</span>
                        <span className="feature-item-text">Area</span>
                      </Trans>
                    </li>
                    <li className="feature-item">
                      <i className="w1-icon business-centre" />
                      <Trans i18nKey="w1:w1.amenities.business_centre">
                        <span className="feature-item-text">Business</span>
                        <span className="feature-item-text">Centre</span>
                      </Trans>
                    </li>
                    <li className="feature-item">
                      <i className="w1-icon gym" />
                      <Trans i18nKey="w1:w1.amenities.fitness_gym">
                        <span className="feature-item-text">Fitness</span>
                        <span className="feature-item-text">Gym</span>
                      </Trans>
                    </li>
                    <li className="feature-item">
                      <i className="w1-icon yoga-room" />
                      <Trans i18nKey="w1:w1.amenities.ping_pong_yoga_room">
                        <span className="feature-item-text">Ping Pong /</span>
                        <span className="feature-item-text">Yoga Room</span>
                      </Trans>
                    </li>
                  </ul>
                </div>
              </TabPanel>
            </Tabs>
          </div>
        </div>
        <div className="content-item content-footer-picture">
          <Image
            src={Courtyard}
            width={744}
            height={465}
            placeholder="blur"
            alt="W1 Rental Apartments Vancouver Courtyard"
          />
        </div>
      </section>
      <section className="content-footer spacing-top container content-footer-picture">
        <div className="content-item left">
          <Image
            src={W1Map}
            width={696}
            height={417}
            placeholder="blur"
            alt="W1 Apartments Vancouver Map"
          />
        </div>
        <div className="content-item app-buttons">
          <div className="full liv-info">
            <h2 className="secondary-title">
              {t('w1:Book your showings & apply now')}
            </h2>
            <div className="liv-marketing">
              <LivLogo width={72} height={32} aria-label="Liv.rent Logo" />
              <a href="../" className="secondary-title">
                www.liv.rent
              </a>
            </div>
            <AppBadgeWrapper>
              <a
                href="https://click.google-analytics.com/redirect?tid=UA-113063682-1&url=https%3A%2F%2Fitunes.apple.com%2Fca%2Fapp%2Fliv%2Fid1321741040&aid=ca.livstrategies.ether&idfa=%{idfa}&cs=w1&cm=index&cn=on-page"
                className="ios ios-button"
                rel="noopener noreferrer"
                target="_blank"
              >
                <AppleButton height={50} />
              </a>
              <a
                href="https://play.google.com/store/apps/details?id=rent.liv.ether&referrer=utm_source%3Dw1%26utm_medium%3Dindex%26utm_campaign%3Don-page"
                className="android-button"
                rel="noopener noreferrer"
                target="_blank"
              >
                <AndroidButton height={50} />
              </a>
            </AppBadgeWrapper>
          </div>
        </div>
      </section>
      <MapSection />
      <section className="section-disclaimer">
        <p className="disclaimer">
          <Trans i18nKey="w1:Disclaimer">
            **Disclaimer: We do not guarantee any move-in dates at this time.
            The above information, while deemed to be correct, is not
            guaranteed. Leasing availability, rates and timing are subject to
            change. Prompton Real Estate Services Inc. provides professional
            property management services on behalf of the owners of these
            properties. Tenants are not required to pay any fees to Prompton for
            the leasing of these properties. Photos are of display suites only.
            Actual views and finishings may vary from what’s shown. Display
            suite furniture not included. Furnished suites are not guaranteed at
            this time. All illustrations and renderings reflect the artists’
            interpretation of the project only and do not take into account the
            neighbouring buildings, physical structures, streets and landscapes.
            Not all amenities may be available upon completion.
          </Trans>
        </p>
      </section>
      <StickyBar visible={!footerOnScreen} />
      <div ref={footer}>
        <Footer />
      </div>
    </Wrapper>
  )
}

export const getStaticProps = async ({ locale }) => {
  return {
    props: {
      ...(await serverSideTranslations(locale, ['translations', 'w1']))
    }
  }
}

export default W1
